<script>
import { currency } from "@/helpers/filters";
import IconButton from "@/components/shared/IconButton.vue";
import { isCoownerOrOwner } from "@/helpers/permissions/loanables";
import { isBorrower } from "@/helpers/permissions/loans";
import { sections } from "@/components/Loan/FullLoanSections";

export default {
  name: "LoanPriceSummary",
  components: { IconButton },
  props: {
    loan: {
      type: Object,
      required: true,
    },
    isNewLoan: {
      type: Boolean,
      default: false,
    },
    borrowerInvoice: {
      type: Object,
      default: () => null,
    },
    ownerInvoice: {
      type: Object,
      default: () => null,
    },
    estimating: {
      type: Boolean,
      default: false,
    },
    estimatedDistance: {
      type: Boolean,
      default: false,
    },
    estimatedDuration: {
      type: Boolean,
      default: false,
    },
    estimatedContribution: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sections,
    };
  },
  computed: {
    isBorrower() {
      return isBorrower(this.$store.state.user, this.loan);
    },
    isCoownerOrOwner() {
      return isCoownerOrOwner(this.$store.state.user, this.loan.loanable);
    },
    showOwnerInvoice() {
      return this.isCoownerOrOwner && !this.isBorrower;
    },
    currentTotal() {
      if (this.isNewLoan) {
        return null;
      }

      if (this.showOwnerInvoice) {
        return this.loan.owner_invoice?.user_balance_change ?? 0;
      }
      return -(this.loan.borrower_invoice?.user_balance_change ?? 0);
    },
    newTotal() {
      if (this.showOwnerInvoice) {
        return this.ownerInvoice?.user_balance_change ?? 0;
      }
      return -(this.borrowerInvoice?.user_balance_change ?? 0);
    },
    hasInvoice() {
      if (this.showOwnerInvoice) {
        return !!this.loan.owner_invoice;
      }
      return !!this.loan.borrower_invoice;
    },
    totalChanged() {
      return this.hasInvoice && !this.isNewLoan && this.newTotal !== this.currentTotal;
    },
    label() {
      if (this.loan.status !== "completed" && this.loan.status !== "validated") {
        if (this.showOwnerInvoice) {
          return this.$t("components.loan.price_summary.estimated_compensation");
        }
        return this.$t("components.loan.price_summary.estimated_cost");
      }

      if (this.showOwnerInvoice) {
        return this.$t("components.loan.price_summary.compensation");
      }
      return this.$t("components.loan.price_summary.cost");
    },
  },
  methods: {
    currency,
  },
};
</script>

<template>
  <div class="price-summary-card">
    {{ label }}
    (<b-link @click="$emit('jump', sections.cost)">{{
      $t("components.loan.price_summary.details")
    }}</b-link
    >)

    <div class="price-summary">
      <span v-if="totalChanged || showOwnerInvoice || newTotal !== 0" class="total">
        {{ newTotal | currency }}{{ totalChanged ? "*" : "" }}
      </span>
      <span v-else class="total">{{ $t("components.loan.price_summary.free") }}</span>

      <span v-if="totalChanged" class="old-total">{{ currentTotal | currency }}</span>

      <b-spinner v-if="estimating" class="spinner" small />
    </div>

    <div v-if="totalChanged" class="included-changes">
      <hr class="my-2" />
      * {{ $t("components.loan.price_summary.includes_changes") }}

      <span>
        <icon-button
          v-if="estimatedDuration"
          size="inline"
          variant="ghost-secondary"
          @click="$emit('jump', sections.dates)"
        >
          {{ $t("components.loan.price_summary.dates") }}
        </icon-button>

        <span v-if="estimatedDuration && (estimatedDistance || estimatedContribution)"
          >{{ estimatedContribution ? ", " : " " + $t("components.loan.price_summary.and") + " " }}
        </span>

        <icon-button
          v-if="estimatedDistance"
          size="inline"
          variant="ghost-secondary"
          @click="$emit('jump', sections.info)"
        >
          {{ $t("components.loan.price_summary.distance_traveled") }}
        </icon-button>

        <span v-if="estimatedContribution && (estimatedDistance || estimatedDuration)">
          {{ $t("components.loan.price_summary.and") }}
        </span>

        <icon-button
          v-if="estimatedContribution"
          size="inline"
          variant="ghost-secondary"
          @click="$emit('jump', sections.cost)"
        >
          {{ $t("components.loan.price_summary.contribution") }}</icon-button
        >.
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.price-summary-card {
  padding: 1rem;
  background: $white;
  border: 1px solid $light-grey;
  border-radius: 1rem;
  position: relative;
}
.included-changes {
  font-size: 0.925rem;
}
.price-summary {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;

  .label {
    padding-right: 1rem;
    font-style: italic;
    text-align: right;
  }

  .invoice-items + .total {
    padding-top: 0.25rem;
    border-top: 1px solid black;
  }
}

.total {
  color: $primary;
  font-size: 1.125rem;
  font-weight: bold;
  text-align: right;
}
.old-total {
  color: $grey;
  text-decoration-line: line-through;
  text-decoration-thickness: 0.1rem;
}

.spinner {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  color: $grey;
}
</style>
